import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, MailCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import Logo from "@/components/Logo";

const box = "w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary";
const btn = "w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50";

function useTimer(initial) {
  const [end, setEnd] = useState(Date.now() + initial * 1000);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return [Math.max(0, Math.round((end - now) / 1000)), (s) => setEnd(Date.now() + s * 1000)];
}

function EmailStep({ email, setEmail, onSent }) {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); toast.success(data.message); onSent(data); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4 mt-8" data-testid="forgot-email-step">
      <input required type="email" placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className={box} />
      <button disabled={busy} data-testid="forgot-send-code" className={btn}>{busy ? "Sending…" : "Send reset code"}</button>
    </form>
  );
}

function ResetStep({ email, info, onBack, onDone }) {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useTimer(info.expires_in || 600);
  const [cool, setCool] = useTimer(info.resend_in || 60);
  const refs = useRef([]);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  const setAt = (i, v) => {
    const c = v.replace(/\D/g, "");
    if (c.length > 1) { const n = c.slice(0, 6).split(""); setDigits([...n, ...Array(6 - n.length).fill("")]); refs.current[Math.min(n.length, 5)]?.focus(); return; }
    const n = [...digits]; n[i] = c; setDigits(n); if (c && i < 5) refs.current[i + 1]?.focus();
  };
  const submit = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try { const { data } = await api.post("/auth/reset-password", { email, otp, new_password: pw }); toast.success(data.message); onDone(); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const resend = async () => {
    try { const { data } = await api.post("/auth/forgot-password", { email }); setCool(data.resend_in || 60); setLeft(data.expires_in || 600); setDigits(Array(6).fill("")); toast.success("New code sent. The previous code no longer works"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-reset-step">
      <div className="text-center"><MailCheck className="h-8 w-8 text-primary mx-auto" />
        <p className="text-sm text-muted-foreground mt-2">If <span className="text-foreground font-medium">{email}</span> has an account, we sent a 6-digit code.</p>
        <p className={`text-xs mt-1 ${left ? "text-muted-foreground" : "text-destructive"}`} data-testid="forgot-expiry-timer">{left ? `Code expires in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}` : "Code expired. Request a new one"}</p>
      </div>
      <div className="flex justify-center gap-2">
        {digits.map((d, i) => <input key={i} ref={(el) => (refs.current[i] = el)} value={d} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
          onChange={(e) => setAt(i, e.target.value)} onKeyDown={(e) => e.key === "Backspace" && !digits[i] && i > 0 && refs.current[i - 1]?.focus()}
          data-testid={`forgot-otp-${i}`} className="h-12 w-11 text-center text-xl font-bold bg-background border border-border rounded-xl outline-none focus:border-primary" />)}
      </div>
      <div className="relative">
        <input type={show ? "text" : "password"} placeholder="New password (min 6 characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-new-password" className={box} />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3.5 text-muted-foreground" aria-label="Toggle password">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
      <input type={show ? "text" : "password"} placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="forgot-confirm-password" className={box} />
      <button disabled={busy || !left} data-testid="forgot-reset-submit" className={btn}>{busy ? "Updating…" : "Reset password"}</button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground" data-testid="forgot-change-email">Change email</button>
        <button type="button" onClick={resend} disabled={cool > 0} className="text-primary disabled:text-muted-foreground" data-testid="forgot-resend">{cool > 0 ? `Resend in ${cool}s` : "Resend code"}</button>
      </div>
    </form>
  );
}

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState(null);
  return (
    <div className="min-h-[85vh] grid place-items-center px-4 py-12 fade-in" data-testid="forgot-password-page">
      <div className="w-full max-w-md rounded-3xl border border-border surface-2 p-8">
        <Logo />
        <div className="flex items-center gap-2 mt-6"><KeyRound className="h-5 w-5 text-primary" /><h1 className="text-2xl font-display font-bold">Forgot password?</h1></div>
        <p className="text-sm text-muted-foreground mt-1">{info ? "Enter the code and choose a new password." : "Enter your email and we'll send you a reset code."}</p>
        {info ? <ResetStep key={info.at} email={email.toLowerCase()} info={info} onBack={() => setInfo(null)} onDone={() => nav("/login")} />
          : <EmailStep email={email} setEmail={setEmail} onSent={(d) => setInfo({ ...d, at: Date.now() })} />}
        <p className="text-center text-sm text-muted-foreground mt-6">Remembered it? <Link to="/login" className="text-primary font-medium" data-testid="forgot-back-login">Sign in</Link></p>
      </div>
    </div>
  );
}
